import { type ReactNode } from 'react'
import { Badge } from '@highlight/ui'
import { twMerge } from 'tailwind-merge'

/** The run of tags at the end of a To-dos row: everything the row says besides its title. */
export function TodoTags({ children }: { children: ReactNode }) {
  return <span className="flex shrink-0 items-center gap-2 self-center">{children}</span>
}

/**
 * One tag: a label, a 12px glyph, or both, in quiet secondary text. `badge` sets one apart on a
 * wash, for the one fact that has to stand out from the row, like an overdue date.
 */
export function TodoTag({
  icon,
  children,
  label,
  badge = false,
  className,
}: {
  icon?: ReactNode
  children?: ReactNode
  /** The accessible name, for a glyph alone or a tag whose colour says more than its text. */
  label?: string
  badge?: boolean
  className?: string
}) {
  if (!badge) {
    return (
      <span
        aria-label={label}
        className={twMerge(
          'text-foreground-secondary type-ui-sm flex items-center gap-1 whitespace-nowrap [&>svg]:size-3 [&>svg]:shrink-0',
          className,
        )}
      >
        {icon}
        {children}
      </span>
    )
  }

  return (
    <Badge
      size="xs"
      left={icon}
      aria-label={label}
      className={twMerge(
        // A rounded rectangle, not the pill: a tag, where a pill reads as a control or a count.
        'text-foreground-secondary rounded-[5px] font-normal',
        children == null && 'w-[var(--badge-h)] px-0 has-[>img:first-child]:pl-0 has-[>svg:first-child]:pl-0',
        className,
      )}
    >
      {children}
    </Badge>
  )
}
